import React from 'react';

import { Language, SeatsioSeatingChart } from '@seatsio/seatsio-react';
import {
  ChartRendererCallbacks,
  MultiLevelPricing,
  SeatingChart,
  SelectableObject,
} from '@seatsio/seatsio-types';
import { Discounts, Selections } from './types';
import { PromoCodeListener } from './PromoCodeListener';
import { pricingMagic } from './utils';

interface SeatedPlanProps {
  pricing: MultiLevelPricing[];
  workspaceKey: string;
  eventId: string;
  currencySymbol: string;
  language: Language;
  selections?: Selections;
  showLegend: boolean;
  showCategoryName: boolean;
  ticketsMaxQtys: Record<string, number>;
}

export const SeatsChart: React.FC<SeatedPlanProps> = ({
  pricing: initialPricing,
  workspaceKey,
  eventId,
  currencySymbol,
  language,
  showLegend,
  showCategoryName,
  ticketsMaxQtys,
}) => {
  const [renderChart, setRenderChart] = React.useState<boolean>(false);
  const [pricing, setPricing] = React.useState(initialPricing);
  const [showPromoCodes, setShowPromoCodes] = React.useState(false);
  const [chart, setChart] = React.useState<SeatingChart | undefined>(undefined);
  const [discounts, setDiscounts] = React.useState<Discounts>([]);
  const [selections, setSelections] = React.useState<Selections>([]);

  // Chart re-renders when pricing changes, so we use a memoized version of the pricing
  // But we need to that it's correct if discounts are applied before its rendered
  const { pricing: staticPricing, availableCategories } = React.useMemo(() => {
    return {
      pricing,
      availableCategories: pricing
        .filter(category => category.ticketTypes.length >= 1)
        .map(category => category.category),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderChart]);

  React.useEffect(() => {
    const pricing = pricingMagic(showPromoCodes, discounts, ticketsMaxQtys, selections);
    chart?.changeConfig({
      pricing: pricing,
      // it seems that if there is a selection and the category becomes unavailable, it will be deselected, so no need to keep this updated based on pricing
      // availableCategories: pricing.filter(category => category.ticketTypes.length >= 1).map(category => category.category),
    });
    setPricing(pricing);
  }, [chart, discounts, selections, showPromoCodes, ticketsMaxQtys]);

  // Clear the tickets form on initial render
  React.useEffect(() => {
    const clearTicketsForm = () => {
      const $ticketsForm = window.$('#tickets .ticket');
      if ($ticketsForm.length) {
        $ticketsForm.find('input').val(0);
        $ticketsForm.trigger('before_change_count');
      }
    };
    clearTicketsForm();
  }, []);

  // JS/Jquery callbacks on the select seats button for initializing the chart and scrolling to it
  React.useEffect(() => {
    const scrollToSeatsSection = () => {
      const seatsSection = window.$('#seats');
      const seatsChartOffset = seatsSection.offset();
      if (seatsChartOffset) {
        // If seats chart is present, smooth scroll to it
        $('html, body').animate(
          {
            scrollTop: seatsChartOffset.top - 50,
          },
          500,
        );
      }
    };

    const initChart = () => {
      const seatsSection = window.$('#seats');
      seatsSection.removeClass('hide');
      setRenderChart(true);
    };

    const selectSeatsButton = window.$('button.select-seats');
    const selectSeatsRow = window.$('#seated-tickets-start');
    const sellableSeatedTickets = window.$('.ticket.seated-ticket:not(.single-ticket-sale)');

    sellableSeatedTickets.on('click', () => {
      initChart();
      scrollToSeatsSection();
    });

    selectSeatsButton.on('click', () => {
      const sellableSeatedTicketsNotPromo = window.$(
        '.ticket.seated-ticket:not(.promo-code-ticket):not(.hide)',
      );
      sellableSeatedTicketsNotPromo.addClass('foo');
      sellableSeatedTicketsNotPromo.removeClass('hide');
      selectSeatsRow.addClass('hide');
      initChart();
      scrollToSeatsSection();
    });
  }, []);

  React.useEffect(() => {
    if (window.seatsioSeatingCharVars) {
      window.seatsioSeatingCharVars.selections = selections;
    }
    const $BuyButton = window.$('button.buy-tickets');
    // If we want to hide all select seats buttons: // const $SelectSeatsButton = window.$('button.select-seats');
    const $SelectSeatsButton = window.$('.select-seats.select-seats-hide-on-selected');
    if (!$BuyButton.hasClass('mixed-seated-tickets')) {
      $BuyButton.toggleClass('hide', !selections.length);
    }

    $SelectSeatsButton.toggleClass('hide', !!selections.length);
  }, [selections, showPromoCodes]);

  const onTicketChange = React.useCallback(
    (ticketType: string, increase: boolean, selectedObject: SelectableObject & { id: string }) => {
      const ticketId = ticketType.endsWith('-p') ? ticketType.slice(0, -2) : ticketType;

      const $promo_ticket = window.$(`div[data-id="${ticketId}"][is_promocode="true"]`);
      const $old_ticket = window.$(`div[data-id="${ticketId}"][is_promocode="false"]`);
      const $was_promocode_applied = $promo_ticket.attr('applied') == 'true';

      const $ticket = $was_promocode_applied ? $promo_ticket : $old_ticket;
      const $ticket_input = $ticket.find('input');
      $ticket_input.val(Math.max(0, parseInt(String($ticket_input.val())) + (increase ? 1 : -1)));
      $ticket.trigger('before_change_count');
      setSelections(prevSelections => {
        if (increase) {
          return [
            ...prevSelections,
            {
              id: selectedObject.id, // This is the object ID in the chart, seems their typing is off
              ticketType: ticketType,
            },
          ];
        }
        return prevSelections.filter(selection => selection.id !== selectedObject.id);
      });
    },
    [
      /* window.$ is a stable global reference */
    ],
  );

  const onObjectSelectedHandler = React.useCallback<
    NonNullable<ChartRendererCallbacks['onObjectSelected']>
  >(
    (selectedObject, selectedTicketType) => {
      onTicketChange(
        selectedTicketType.ticketType,
        true,
        selectedObject as SelectableObject & { id: string },
      );
    },
    [onTicketChange],
  );

  const onObjectDeselectedHandler = React.useCallback<
    NonNullable<ChartRendererCallbacks['onObjectSelected']>
  >(
    (selectedObject, selectedTicketType) => {
      onTicketChange(
        selectedTicketType.ticketType,
        false,
        selectedObject as SelectableObject & { id: string },
      );
    },
    [onTicketChange],
  );

  const handleOnChartRendered = React.useCallback((chart: SeatingChart) => {
    if (window.seatsioSeatingCharVars) {
      window.seatsioSeatingCharVars.chart = chart;
    }
    setChart(chart);
  }, []);

  // keep holdToken in sync with window.seatsioSeatingCharVars
  React.useEffect(() => {
    if (window.seatsioSeatingCharVars) {
      window.seatsioSeatingCharVars.holdToken = chart?.holdToken || '';
    }
  }, [chart?.holdToken]);

  // Because we don't want to deal with keeping the form in sync with selections on refresh
  // We send the hold token to the server when the page is unloaded, to cancel the hold
  // If a purchase exists with the hold token, it doesn't cancel the hold
  React.useEffect(() => {
    const handlePageUnload = () => {
      if (chart?.holdToken) {
        const url = `/seats/cancel/`;
        const data = JSON.stringify({ hold_token: chart?.holdToken });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    };

    window.addEventListener('beforeunload', handlePageUnload);

    return () => {
      window.removeEventListener('beforeunload', handlePageUnload);
    };
  }, [chart?.holdToken]);

  return (
    <>
      <PromoCodeListener setDiscounts={setDiscounts} setShowPromoCodes={setShowPromoCodes} />
      {renderChart && (
        <SeatsioSeatingChart
          session={'start'} // We use 'start' rather than 'continue' as otherwise we would need to deal with having
          showFullScreenButton={false}
          language={language}
          workspaceKey={workspaceKey}
          event={eventId}
          region="eu"
          mode={'normal'}
          onObjectSelected={onObjectSelectedHandler}
          onObjectDeselected={onObjectDeselectedHandler}
          pricing={staticPricing}
          availableCategories={availableCategories}
          showLegend={showLegend}
          legend={{
            hideNonSelectableCategories: true,
            hideUnavailableLegendItem: true,
            hidePricing: false,
            hideCategoryName: !showCategoryName,
          }}
          objectPopover={{
            showAvailability: false,
            showCategory: showCategoryName,
            showLabel: true,
            showPricing: true,
            showUnavailableNotice: true,
            stylizedLabel: true,
            confirmSelection: 'auto',
            confirmTicketTypeSelection: true,
          }}
          priceFormatter={price => {
            if (price === 0) {
              return window.seatsioSeatingCharVars?.transFree || 'Free';
            }
            return `${currencySymbol}${price.toFixed(2)}`;
          }}
          onChartRendered={handleOnChartRendered}
        />
      )}
    </>
  );
};
